import type { Recording, TakeSummary } from "./capture";

export const TAKE_PAGE_SIZE = 12;

async function database(): Promise<IDBDatabase> {
  return new Promise(function (resolve, reject) {
    const request = indexedDB.open("cue-recordings", 2);
    let blocked = false;
    request.onupgradeneeded = function () {
      const db = request.result;
      if (!db.objectStoreNames.contains("takes")) {
        db.createObjectStore("takes", { keyPath: "id" });
      }
      const summaries = db.createObjectStore("summaries", { keyPath: "id" });
      summaries.createIndex("chronological", ["created", "id"]);
      // Upgrade existing recordings atomically without rewriting their video bytes.
      const upgrade = request.transaction!;
      const recordings = upgrade.objectStore("takes");
      const cursorRequest = recordings.openCursor();
      cursorRequest.onsuccess = function () {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        const { blob, ...summary } = cursor.value as Recording;
        summaries.put(summary);
        cursor.continue();
      };
    };
    request.onblocked = function () {
      blocked = true;
      reject(new Error("Close other Cue tabs to upgrade recording storage."));
    };
    request.onsuccess = function () {
      const db = request.result;
      db.onversionchange = function () {
        db.close();
      };
      if (blocked) db.close();
      else resolve(db);
    };
    request.onerror = function () {
      reject(request.error);
    };
  });
}

/** Run requests in one transaction and expose results only after commit. */
async function transact<T>(
  stores: string[],
  mode: IDBTransactionMode,
  run: (transaction: IDBTransaction) => T,
): Promise<T> {
  const db = await database();
  try {
    return await new Promise(function (resolve, reject) {
      const transaction = db.transaction(stores, mode);
      const result = run(transaction);
      transaction.oncomplete = function () {
        resolve(result);
      };
      transaction.onerror = function () {
        reject(transaction.error);
      };
      transaction.onabort = function () {
        reject(transaction.error);
      };
    });
  } finally {
    db.close();
  }
}

/** Newest first. The compound cursor keeps equal timestamps stable across pages. */
export async function listTakes(before?: TakeSummary): Promise<{
  takes: TakeSummary[];
  total: number;
  hasMore: boolean;
}> {
  const result = await transact(
    ["summaries"],
    "readonly",
    function (transaction) {
      const store = transaction.objectStore("summaries");
      const count = store.count();
      const range = before
        ? IDBKeyRange.upperBound([before.created, before.id], true)
        : null;
      const index = store.index("chronological");
      const request = index.openCursor(range, "prev");
      const entries: TakeSummary[] = [];
      request.onsuccess = function () {
        const cursor = request.result;
        if (!cursor) return;
        entries.push(cursor.value);
        if (entries.length <= TAKE_PAGE_SIZE) cursor.continue();
      };
      return { count, entries };
    },
  );
  return {
    takes: result.entries.slice(0, TAKE_PAGE_SIZE),
    total: result.count.result,
    hasMore: result.entries.length > TAKE_PAGE_SIZE,
  };
}

export async function getTake(id: string): Promise<Recording> {
  const request = await transact(["takes"], "readonly", function (transaction) {
    const store = transaction.objectStore("takes");
    return store.get(id) as IDBRequest<Recording | undefined>;
  });
  if (!request.result)
    throw new Error("This take is no longer stored on this device.");
  return request.result;
}

export async function saveTake(take: Recording): Promise<void> {
  await transact(["takes", "summaries"], "readwrite", function (transaction) {
    const { blob, ...summary } = take;
    transaction.objectStore("takes").put(take);
    transaction.objectStore("summaries").put(summary);
  });
}

export async function deleteTake(id: string): Promise<void> {
  await transact(["takes", "summaries"], "readwrite", function (transaction) {
    transaction.objectStore("takes").delete(id);
    transaction.objectStore("summaries").delete(id);
  });
}
